import { useRef, useState, useEffect } from 'react';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Models
import IQuiz from 'models/Quiz';
// Types
import QuizTypes from 'types/QuizTypes';
// Actions
import { setQuestionStart, setQuestionEnd } from 'store/player/playerSlice';
// Selectors
import { selectPreviewPageMode, selectQuestionStart } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';

const useQuestionInterval = (questionMaxScore:number) => {
  const dispatch = useAppDispatch();

  const quiz:IQuiz = useAppSelector(selectQuiz);
  const questionStart:boolean = useAppSelector(selectQuestionStart);
  const previewPageMode:boolean = useAppSelector(selectPreviewPageMode);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const defaultTimeout = quiz.type === QuizTypes.Prediction ? 30 : 10;
  const timeout = quiz.questionsSettings.timeout || defaultTimeout;
  const maxScore = questionMaxScore || (timeout * 100);
  const scorePerMs = maxScore / (timeout * 1000);

  const [ score, setScore ] = useState(maxScore);
  const [ time, setTime ] = useState<number>(0);

  const width = (score / maxScore) * 100;

  const clear = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (previewPageMode) return;

    if (questionStart) {
      startTimeRef.current = Date.now();

      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsedMs = now - (startTimeRef.current ?? now);
        
        if (quiz.questionsSettings.timeout > 0) {
          const newScore = Math.max(0, Math.round(maxScore - elapsedMs * scorePerMs));
          setScore(newScore);
        }
        
        setTime(elapsedMs / 1000);
      }, 50);
    } else {
      clear();
    }

    return clear;
    // eslint-disable-next-line
  }, [questionStart]);

  useEffect(() => {
    if (score <= 0) {
      setScore(0);
      dispatch(setQuestionStart(false));
      dispatch(setQuestionEnd(true));
    }
    // eslint-disable-next-line
  }, [score]);

  return { score, time, width };
}

export default useQuestionInterval;
